import { Flex, Segmented, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import { kpiTrend } from './activityData'
import { useStyles } from './styles'
import type { GraphKpis, InsightPeriod } from './types'

const { Text } = Typography

const Sparkline = ({ data, color }: { data: number[]; color: string }) => {
  const width = 88
  const height = 22
  const min = Math.min(...data)
  const max = Math.max(...data)
  const range = max - min || 1
  const step = width / (data.length - 1 || 1)
  const pts = data.map((v, i) => [
    i * step,
    height - ((v - min) / range) * (height - 3) - 1.5
  ])
  const line = pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0]},${p[1]}`)
    .join(' ')
  const area = `${line} L${width},${height} L0,${height} Z`

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: 'block' }}
      aria-hidden
    >
      <path d={area} fill={color} opacity={0.12} />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth={1.4}
        strokeLinecap="round"
      />
    </svg>
  )
}

type Props = {
  kpis: GraphKpis
  period: InsightPeriod
  onPeriodChange: (p: InsightPeriod) => void
}

export const KpiStrip = ({ kpis, period, onPeriodChange }: Props) => {
  const { styles } = useStyles()
  const { t } = useTranslation()

  const tiles: {
    key: string
    label: string
    value: number
    alert?: boolean
    /** for this metric, is an increase good? */
    upIsGood: boolean
  }[] = [
    {
      key: 'activeSessions',
      label: t('workspaceOverview.companyGraph.kpi.activeSessions'),
      value: kpis.activeSessions,
      upIsGood: true
    },
    {
      key: 'eventsPerMin',
      label: t('workspaceOverview.companyGraph.kpi.eventsPerMin'),
      value: kpis.eventsPerMin,
      upIsGood: true
    },
    {
      key: 'agentsRunning',
      label: t('workspaceOverview.companyGraph.kpi.agentsRunning'),
      value: kpis.agentsRunning,
      upIsGood: true
    },
    {
      key: 'openAlerts',
      label: t('workspaceOverview.companyGraph.kpi.openAlerts'),
      value: kpis.openAlerts,
      alert: kpis.openAlerts > 0,
      upIsGood: false
    }
  ]

  const periodOptions: { label: string; value: InsightPeriod }[] = [
    {
      label: t('workspaceOverview.companyGraph.insights.period.today'),
      value: 'today'
    },
    {
      label: t('workspaceOverview.companyGraph.insights.period.7d'),
      value: '7d'
    },
    {
      label: t('workspaceOverview.companyGraph.insights.period.30d'),
      value: '30d'
    }
  ]

  return (
    <div className={styles.kpiRow}>
      {tiles.map((tile) => {
        const trend = kpiTrend(tile.key, period)
        const good = tile.upIsGood ? trend.deltaPct >= 0 : trend.deltaPct <= 0
        const deltaColor = good
          ? 'var(--ant-color-success)'
          : 'var(--ant-color-error)'

        return (
          <div key={tile.key} className={styles.kpiTile}>
            <Text className={styles.kpiLabel}>{tile.label}</Text>
            <Flex align="baseline" gap={6}>
              <Text
                className={styles.kpiValue}
                style={
                  tile.alert ? { color: 'var(--ant-color-error)' } : undefined
                }
              >
                {tile.value.toLocaleString()}
              </Text>
              <Text className={styles.kpiDelta} style={{ color: deltaColor }}>
                {trend.deltaPct >= 0 ? '▲' : '▼'} {Math.abs(trend.deltaPct)}%
              </Text>
            </Flex>
            <Sparkline
              data={trend.spark}
              color={
                tile.alert
                  ? 'var(--ant-color-error)'
                  : 'var(--ant-color-primary)'
              }
            />
          </div>
        )
      })}

      <div className={styles.kpiPeriod}>
        <Segmented
          size="small"
          value={period}
          onChange={(v) => onPeriodChange(v as InsightPeriod)}
          options={periodOptions}
        />
      </div>
    </div>
  )
}
